import React, {useEffect} from 'react';

import {AnalysisViewGraphQL} from 'analysis-tool/AnalysisTypes';
import {assertAndContinue, assert} from 'Asserts';
import {
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  assertPipelineStepIsStepType,
  pipelineStepIsStepType,
  pipelineStepRawDataIsKind,
} from 'pipelines/PipelineTypes';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import {assertNever} from 'util/Types';

import ChartView from './ChartView';
import DataFrameView from './DataFrameView';
import {PreviewData, useDataViewContext} from './DataViewContext';
import FailedView from './FailedView';
import FileView from './FileView';
import usePreviewDraftStep from './hooks/usePreviewDraftStep';
import RunningView from './RunningView';

/**
 * Generically render an AnalysisView's main UI.
 *
 * The behavior of DataView is configured via the `DataViewContext`. @see `DataViewContextValue` for details on
 * configuration options.
 *
 * The goal of DataView is to render any view in Analyses. For example, this means looking at the steps in the lineage,
 * their status and their types, to generate the main content area by delegating to appropriate subcomponents.
 *
 * Expectations from callers:
 * - Provide a `DataViewContext` to configure the `DataView`.
 * - Use a div with a height and width to contain the `DataView` and give it a size.
 *
 * @see `getComponentForAnalysisView` for details on how the rendered inner-component is picked.
 * @param analysisView - The AnalysisViewGraphQL to render.
 */
export default function DataView(): JSX.Element {
  const {analysisView, previewData} = useDataViewContext();

  assertAndContinue(analysisView.lineage.length > 0, 'Lineage must have at least one node');

  const previewDraftStep = usePreviewDraftStep(
    previewData != null && pipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, previewData.draftStep)
      ? previewData.draftStep
      : null,
    // Do not use the context's clientId, because it can change if more transforms are added and then
    // the preview be stuck in a NOT_FETCHED state
    previewData?.draftStep?.id ?? 'NO_PREVIEW_STEP'
  );

  useEffect(() => {
    if (previewDraftStep != null) {
      assert(previewData != null, 'Preview data must be set if using a draft step');
      previewData?.setPreviewDraftStepData(previewDraftStep);
    }
  }, [previewDraftStep, previewData]);

  const DataViewComponent = getComponentForAnalysisView(analysisView, previewData);

  return <DataViewComponent />;
}

const ASYNC_STATUS_TO_STEP_STATUS_CODE = {
  [AsyncStatus.NOT_FETCHED]: PipelineStepStatusCode.WAITING,
  [AsyncStatus.LOADING]: PipelineStepStatusCode.RUNNING,
  [AsyncStatus.SUCCESS]: PipelineStepStatusCode.SUCCEEDED,
  [AsyncStatus.ERROR]: PipelineStepStatusCode.FAILED,
};

/**
 * Return the component that should be rendered for the given analysis view.
 *
 * The returned component is dictated by the last step in the lineage or by the preview draft step
 *
 * If there is no preview data, we look at both it's status and output type of the last step in the lineage:
 * - Status = FAILED: `FailedView`
 * - Status = BLOCKED | RUNNING | WAITING: `RunningView`
 * - Status = SUCCEEDED: Component is determined by the output raw datatype:
 *   - ChartNode: `ChartView`
 *   - DataFrameDomainNode: `DataFrameView`
 *   - FileNode: `FileView`
 *
 * If there is a preview data, we look at the status of the preview (its generate through a celery task) and
 * the step type being previewed.
 *
 * @package
 */
function getComponentForAnalysisView({lineage}: AnalysisViewGraphQL, previewData?: PreviewData): React.FC {
  if (!assertAndContinue(lineage.length > 0, 'Lineage must have at least one node')) {
    return FailedView;
  }

  const maybePreviewDraftStep = previewData?.draftStep;
  const maybePreviewStatus = previewData?.previewDraftStepData.previewStatus;
  const renderedLineageNode = lineage[lineage.length - 1];

  const statusCode =
    previewData &&
    maybePreviewStatus &&
    // Only use the async status for previewing charts. Previewing tables can only be done from charts, and we
    // use chart's SINGLE_DATASET_OUTPUT step data
    pipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, previewData.draftStep)
      ? ASYNC_STATUS_TO_STEP_STATUS_CODE[maybePreviewStatus]
      : renderedLineageNode.step.statusCode;

  if (
    statusCode === PipelineStepStatusCode.RUNNING ||
    statusCode === PipelineStepStatusCode.BLOCKED ||
    statusCode === PipelineStepStatusCode.WAITING
  ) {
    return RunningView;
  } else if (statusCode === PipelineStepStatusCode.FAILED) {
    return FailedView;
  } else if (statusCode === PipelineStepStatusCode.SUCCEEDED) {
    if (maybePreviewDraftStep != null) {
      if (pipelineStepIsStepType(PipelineStepType.TABLE_VIEW, 0, maybePreviewDraftStep)) {
        return DataFrameView;
      }
      assertPipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, maybePreviewDraftStep);
      return ChartView;
    }
    if (
      !assertAndContinue(
        renderedLineageNode.stepOutputReference != null,
        'Expected stepOutputReference to be present'
      )
    ) {
      return FailedView;
    }

    const stepOutputReference = renderedLineageNode.stepOutputReference!;

    // TODO(BNCH-120876): DataView component currently assumes most standard step types that have outputs like chart, but
    // doesn't handle step types with special "ui views" like `EXPORT_DATA_CATALOG_DATASET` or steps without outputs like
    // `SEND_TO_DOCUMENT`. DataView should handle rendering any Views, so we should consider how to handle those cases.
    if (
      !assertAndContinue(
        stepOutputReference.rawDataIndex != null,
        'Expected stepOutputReference to have rawDataIndex'
      )
    ) {
      return FailedView;
    }

    const outputStepData = stepOutputReference.outputStepData;
    const rawDataIndex = stepOutputReference.rawDataIndex!;

    if (!assertAndContinue(outputStepData.rawDatas.length > rawDataIndex, 'rawDataIndex is out of bounds')) {
      return FailedView;
    }

    const rawData = outputStepData.rawDatas[rawDataIndex];

    if (pipelineStepRawDataIsKind(rawData, PipelineStepDataKind.CHART)) {
      return ChartView;
    } else if (pipelineStepRawDataIsKind(rawData, PipelineStepDataKind.DATASET)) {
      return DataFrameView;
    } else if (pipelineStepRawDataIsKind(rawData, PipelineStepDataKind.FILE)) {
      return FileView;
    } else {
      throw new Error(`Unknown output type: ${assertNever(rawData)}`);
    }
  } else {
    throw new Error(`Unknown status code: ${assertNever(statusCode)}`);
  }
}
